import { useState } from "react";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, MapPin, Clock, CalendarDays, Facebook, Instagram, Linkedin, Check, Minus } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EVENTS = [
  {
    slug: "masterclass",
    day: "12", month: "November", year: "2026",
    title: "Afternoon Marketing MasterClass with Marc",
    date: "Thursday, November 12, 2026",
    time: "3:00 PM – 5:00 PM",
    venue: "Cut 132, Columbus",
    image: "https://uploads.tickettailorassets.com/c_scale,h_400,q_85,w_800/v1/production/userfiles/pxi7puxptdjbvlemdjff.jpg",
    tickets: "https://www.tickettailor.com/events/devotedweddings/2435461",
  },
  {
    slug: "elevate",
    day: "12", month: "November", year: "2026",
    title: "Elevate & Celebrate — Speaker, Social, & Growth for Wedding Pros",
    date: "Thursday, November 12, 2026",
    time: "6:00 PM – 8:30 PM",
    venue: "Cut 132, Columbus",
    image: "https://uploads.tickettailorassets.com/c_scale,h_400,q_85,w_800/v1/production/userfiles/bcjqz876lwl0uea86d1f.jpg",
    tickets: "https://www.tickettailor.com/events/devotedweddings/2118815",
  },
  {
    slug: "holiday",
    day: "14", month: "December", year: "2026",
    title: "A Smashing Holiday Party for Wedding Pros",
    date: "Monday, December 14, 2026",
    time: "6:00 PM – 9:00 PM",
    venue: "Smash Park",
    image: "https://uploads.tickettailorassets.com/c_scale,h_400,q_85,w_800/v1/production/userfiles/k9nonoybm8t9f6uqac69.jpg",
    tickets: "https://www.tickettailor.com/events/devotedweddings/2118816",
  },
  {
    slug: "moneymap",
    day: "12", month: "January", year: "2027",
    title: "Wedding Pros with a Plan — Your 12 Month Money Map",
    date: "Tuesday, January 12, 2027",
    time: "6:00 PM – 8:30 PM",
    venue: "St. Charles Prep School — Walter Commons, Bexley",
    image: "https://uploads.tickettailorassets.com/c_scale,h_400,q_85,w_800/v1/production/userfiles/yik7rg7vxtmilqd14ejl.jpg",
    tickets: "https://www.tickettailor.com/events/devotedweddings/2118814",
  },
];

const BOARD = [
  { role: "President", name: "Jamie Rapavy", company: "Devoted Weddings", img: "/leader-jamie.webp" },
  { role: "Vice President", name: "Julie Hedrick", company: "Blue Skies HD Video & Film", img: "/leader-julie.jpg" },
  { role: "Secretary", name: "Jody Unger", company: "Hilton Downtown", img: "/leader-jody.jpg" },
  { role: "Programs", name: "Jessica Garrabrant", company: "Generation Tux", img: "/leader-jessica.jpg" },
  { role: "Membership", name: "Jalana Caughey", company: "Le Méridien", img: "/leader-jalana.webp" },
  { role: "Social Media / Outreach", name: "Candice Rains", company: "Flawless", img: "/leader-candice.jpg" },
  { role: "Social Media / Outreach", name: "Courtney Price", company: "Loft 18", img: "/leader-courtney.jpg" },
];

const FEELINGS = [
  "Your bookings are inconsistent",
  "Networking feels awkward or unproductive",
  "You're tired of chasing leads",
  "You know relationships matter, but you're not sure how to build the right ones",
  "Marketing feels overwhelming or expensive",
  "You feel like you're doing this business alone",
  "You want to stay visible and top of mind with other wedding pros",
  "You want better referrals, not just more random leads",
];

const OPPORTUNITIES = [
  "Build meaningful referral relationships",
  "Connect with wedding professionals who understand your business",
  "Stay visible and remembered in the industry",
  "Gain practical business education and ideas",
  "Access member-only events and experiences",
  "Collaborate instead of always feeling like you're competing",
  "Get support, introductions, and opportunities you may not find on your own",
  "Grow through relationships instead of relying only on paid marketing",
];

const TIERS = [
  {
    name: "Devoted Member",
    badge: "",
    price: "$299",
    priceNote: "Founding first year · $399 regular",
    reps: "1 company representative",
    featured: false,
    cta: "Become a Member",
    link: "https://buy.stripe.com/7sY28s8JW2XpbAB2VxbMQ01",
  },
  {
    name: "Devoted VIP",
    badge: "Best Value",
    price: "$450",
    priceNote: "Founding first year · $550 regular",
    reps: "2 company representatives",
    featured: true,
    cta: "Become a VIP",
    link: "https://buy.stripe.com/28EfZi9O0gOfawxgMnbMQ02",
  },
];

// member/vip values: true = included, false = not included, string = custom note
const COMPARISON = [
  { feature: "Company representatives", member: "1", vip: "2 — vs. $798 as two memberships" },
  { feature: "Private online community", member: true, vip: true },
  { feature: "Personal referrals through Devoted", member: true, vip: true },
  { feature: "Collaboration + accountability through community connections", member: true, vip: true },
  { feature: "Quarterly Power Hours + other virtual opportunities", member: true, vip: true },
  { feature: "In-person events — coffees, lunches, dinners, Deep Dives, happy hours, Pros at Play, roundtables, workshops + more", member: true, vip: "Included + priority registration" },
  { feature: "Preferred pricing + discounts", member: true, vip: "Deeper discounts" },
  { feature: "Curated couple-facing member list", member: "Name, category + contact", vip: "Featured at top of category with logo + company description" },
  { feature: "Website badge", member: "Member", vip: "VIP" },
  { feature: "VIP Business Roundtable with Jamie — quarterly, capped at 12", member: false, vip: true },
  { feature: "VIP annual experience — like the December VIP dinner", member: false, vip: true },
  { feature: "Devoted Wedding Directory visibility credit", member: false, vip: "$100 credit" },
  { feature: "Social spotlight", member: false, vip: true },
];

const TierCell = ({ value, vip }) => (
  <div className={`py-3 px-3 flex items-center justify-center text-center font-body text-sm ${
    vip ? "bg-[#2C5A63]/[0.05] text-[#2C5A63]" : "text-[#2C5A63]/75"
  }`}>
    {value === true ? (
      <Check size={18} strokeWidth={2.5} className="text-[#2C5A63] shrink-0" />
    ) : value === false ? (
      <Minus size={16} className="text-[#2C5A63]/25 shrink-0" />
    ) : (
      <span className={vip ? "font-semibold" : ""}>{value}</span>
    )}
  </div>
);

const EVENT_PHOTOS = ["/event1.webp", "/event2.webp", "/event3.webp", "/event4.jpg", "/event5.webp", "/event6.webp", "/event7.webp", "/event8.webp", "/event9.webp"];

const INTERESTS = ["Hosting a community event", "Being an educator", "Wedding expos", "Mentorship"];

const connectField =
  "w-full bg-transparent border-b border-[#2C5A63]/25 py-3 font-body text-[#2C5A63] outline-none placeholder:text-[#2C5A63]/40 focus:border-[#2C5A63] transition-colors";

function ConnectForm() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", interest: INTERESTS[0] });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        ...form,
        source: "networking",
        subject: `Networking inquiry — ${form.interest}`,
      });
      toast.success("Thank you — your message is on its way to Devoted Wedding Pros.");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#2C5A63] text-white p-12 text-left" data-testid="connect-success">
        <span className="font-display text-6xl text-[#7BA7AE]">✦</span>
        <h3 className="font-display text-4xl mt-6">Message received.</h3>
        <p className="font-body text-white/80 mt-4">
          Thank you, {form.name.split(" ")[0] || "friend"}. We'll be in touch about {form.interest.toLowerCase()} soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="connect-form" className="text-left space-y-8">
      <div className="grid sm:grid-cols-2 gap-8">
        <input required placeholder="Name" data-testid="connect-name" value={form.name} onChange={set("name")} className={connectField} />
        <input required placeholder="Company" data-testid="connect-company" value={form.company} onChange={set("company")} className={connectField} />
      </div>
      <div className="grid sm:grid-cols-2 gap-8">
        <input required type="email" placeholder="Email" data-testid="connect-email" value={form.email} onChange={set("email")} className={connectField} />
        <input placeholder="Phone" data-testid="connect-phone" value={form.phone} onChange={set("phone")} className={connectField} />
      </div>
      <div>
        <label className="overline text-[#7BA7AE] block mb-3">I'm interested in…</label>
        <div className="flex flex-wrap gap-3">
          {INTERESTS.map((it) => (
            <button
              type="button"
              key={it}
              data-testid={`connect-interest-${it.split(" ")[0].toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, interest: it }))}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
                form.interest === it
                  ? "bg-[#2C5A63] text-white border-[#2C5A63]"
                  : "border-[#2C5A63]/30 text-[#2C5A63] hover:border-[#2C5A63]"
              }`}
            >
              {it}
            </button>
          ))}
        </div>
      </div>
      <textarea rows={4} placeholder="Message" data-testid="connect-message" value={form.message} onChange={set("message")} className={`${connectField} resize-none`} />
      <button
        type="submit"
        disabled={loading}
        data-testid="connect-submit"
        className="rounded-full bg-[#2C5A63] text-white px-10 py-4 font-medium hover:bg-[#21474F] transition-colors disabled:opacity-60"
      >
        {loading ? "Sending…" : "Submit"}
      </button>
    </form>
  );
}

function SuperchargeCTA() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, source: "supercharge", subject: "Supercharge My Membership inquiry" });
      setDone(true);
      toast.success("Sent! We'll be in touch about supercharging your membership.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="supercharge-cta"
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#2C5A63] text-white px-8 py-4 font-medium hover:bg-[#21474F] transition-colors"
      >
        Supercharge My Membership
        <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white border-[#E4E4EA]" data-testid="supercharge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2C5A63] text-left">Supercharge my membership</DialogTitle>
          </DialogHeader>
          {done ? (
            <p className="font-body text-[#2C5A63]/80 py-2" data-testid="supercharge-success">
              Thank you! We'll reach out shortly about getting your business in front of more couples.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-6 mt-2" data-testid="supercharge-form">
              <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="supercharge-name" className={connectField} />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="supercharge-email" className={connectField} />
              <input placeholder="Business / Company" value={form.company} onChange={set("company")} data-testid="supercharge-company" className={connectField} />
              <textarea rows={3} placeholder="Anything you'd like us to know? (optional)" value={form.message} onChange={set("message")} data-testid="supercharge-message" className={`${connectField} resize-none`} />
              <button type="submit" disabled={loading} data-testid="supercharge-submit" className="rounded-full bg-[#2C5A63] text-white px-8 py-3.5 font-medium hover:bg-[#21474F] transition-colors disabled:opacity-60">
                {loading ? "Sending…" : "Send request"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Networking() {
  return (
    <div data-testid="networking-page" className="pt-24">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex bg-[#2C5A63] rounded-2xl p-4 mb-7">
            <img src="/dwp-logo-white.png" alt="Devoted Wedding Pros" className="h-20 w-auto" />
          </div>
          <p className="overline text-[#2C5A63] mb-6">Community · Devoted Wedding Pros — Ohio Chapter</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.2rem] text-[#2C5A63] leading-[0.94] tracking-tight">
            <MaskedLines lines={["Welcome to Devoted", "Wedding Pros."]} />
          </h1>
          <p className="font-body text-lg text-[#2C5A63]/75 leading-relaxed mt-8 max-w-xl">
            We're a vibrant community exclusively for wedding industry professionals across
            Ohio who are passionate about building their businesses through authentic local
            connections. Our mission is simple: bring together talented wedding pros, foster genuine
            collaboration, and create a supportive space where everyone thrives.
          </p>
          <a
            href="https://www.tickettailor.com/events/devotedweddings"
            target="_blank" rel="noreferrer"
            data-testid="networking-hero-cta"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#2C5A63] text-white px-8 py-4 font-medium hover:bg-[#21474F] transition-colors"
          >
            Join us today
            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
        <div className="lg:col-span-5">
          <div className="relative h-[440px] lg:h-[520px] clip-arch overflow-hidden">
            <img src={IMAGES.heroNetworking} alt="Devoted Wedding Pros networking" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <div className="bg-[#7BA7AE]/40 h-2" />

      {/* Events Calendar */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="events-calendar">
        <Reveal className="mb-14">
          <p className="overline text-[#2C5A63] mb-4">Upcoming events</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none max-w-2xl">
            Community Events Calendar
          </h2>
          <p className="font-body text-[#2C5A63]/70 mt-5 max-w-2xl">
            Upcoming DWP mixers, workshops, and industry events happening across Ohio. Ready to grow
            your wedding business with Ohio's most dedicated community of local pros?
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-8">
          {EVENTS.map((e, i) => (
            <Reveal key={e.slug} delay={i * 0.1}>
              <div className="bg-white border border-[#E4E4EA] flex flex-col h-full" data-testid={`event-${e.slug}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={e.image} alt={e.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-4 left-4 bg-[#2C5A63] text-white px-4 py-2 text-center leading-none">
                    <span className="font-display text-3xl block">{e.day}</span>
                    <span className="overline text-[0.6rem] text-white/70">{e.month}</span>
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-display text-2xl text-[#2C5A63] leading-tight">{e.title}</h3>
                  <div className="mt-4 space-y-2 font-body text-sm text-[#2C5A63]/75">
                    <p className="flex items-center gap-2"><CalendarDays size={15} className="text-[#2C5A63]" /> {e.date}</p>
                    <p className="flex items-center gap-2"><Clock size={15} className="text-[#2C5A63]" /> {e.time}</p>
                    <p className="flex items-center gap-2"><MapPin size={15} className="text-[#2C5A63]" /> {e.venue}</p>
                  </div>
                  <a
                    href={e.tickets} target="_blank" rel="noreferrer"
                    data-testid={`event-tickets-${e.slug}`}
                    className="mt-6 inline-flex items-center gap-2 w-fit rounded-full bg-[#2C5A63] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#21474F] transition-colors"
                  >
                    Get {e.month} Event Tickets <ArrowUpRight size={15} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Join */}
      <section className="bg-white py-20 lg:py-28" data-testid="why-join">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-14 max-w-3xl">
            <p className="overline text-[#2C5A63] mb-4">Connect · Grow · Belong</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none">
              More than networking. Relationships create revenue.
            </h2>
            <p className="font-body text-[#2C5A63]/70 mt-5">
              Devoted is a year-round community for wedding pros who want to connect, grow, and
              belong — a place to become known, trusted, and remembered.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-8">
            <Reveal>
              <div className="bg-[#F3F2F5] border border-[#E4E4EA] p-10 h-full">
                <h3 className="font-display text-2xl lg:text-3xl text-[#2C5A63] mb-6">If you're feeling like…</h3>
                <ul className="space-y-3 font-body text-[#2C5A63]/80">
                  {FEELINGS.map((f) => (
                    <li key={f} className="flex gap-3 border-b border-[#E4E4EA] pb-3">
                      <span className="text-[#7BA7AE] mt-0.5">—</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-[#2C5A63] text-white p-10 h-full">
                <h3 className="font-display text-2xl lg:text-3xl mb-6">Inside Devoted, you'll have opportunities to…</h3>
                <ul className="space-y-3 font-body text-white/85">
                  {OPPORTUNITIES.map((o) => (
                    <li key={o} className="flex gap-3 border-b border-white/15 pb-3">
                      <span className="text-[#7BA7AE] mt-0.5">✦</span> {o}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Event Photos */}
      <section className="py-14 lg:py-20 overflow-hidden bg-[#F3F2F5]" data-testid="event-photos">
        <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-10">
          <p className="overline text-[#2C5A63] mb-3">In the room</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C5A63] tracking-tight leading-none">
            Moments from our events
          </h2>
        </Reveal>
        <Marquee speed={40} gradient={false} pauseOnHover autoFill>
          {EVENT_PHOTOS.map((src, i) => (
            <div key={i} className="mx-3 h-64 lg:h-80 overflow-hidden rounded-sm border border-[#E4E4EA]">
              <img src={src} alt="Devoted Wedding Pros event" className="h-full w-auto object-cover" loading="lazy" />
            </div>
          ))}
        </Marquee>
      </section>

      {/* Membership */}
      <section className="bg-white py-20 lg:py-28" data-testid="membership">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-6 text-center">
            <p className="overline text-[#2C5A63] mb-4">Membership investment</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none">
              Choose your membership
            </h2>
            <p className="font-body text-[#2C5A63]/70 mt-5 max-w-xl mx-auto">
              Become a founding member and save $100 on your first year. Founding pricing expires
              November 16th — regular pricing resumes after that.
            </p>
          </Reveal>
          <Reveal className="mt-12">
            <div data-testid="membership-comparison">
              {/* Tier headers */}
              <div className="grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr]">
                <div className="hidden md:block" />
                {TIERS.map((t) => (
                  <div
                    key={t.name}
                    data-testid={`tier-${t.name.split(" ")[1].toLowerCase()}`}
                    className={`relative px-4 py-6 text-center rounded-t-sm ${
                      t.featured
                        ? "bg-[#2C5A63] text-white"
                        : "bg-[#FAF9F7] text-[#2C5A63] border-x border-t border-[#E4E4EA]"
                    }`}
                  >
                    {t.badge ? (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2C5A63] text-white text-xs font-semibold tracking-wide uppercase px-4 py-1.5 rounded-full whitespace-nowrap">
                        {t.badge}
                      </span>
                    ) : null}
                    <p className={`overline ${t.featured ? "text-[#7BA7AE]" : "text-[#2C5A63]"}`}>{t.name}</p>
                    <p className="font-display text-4xl sm:text-5xl mt-2 leading-none">{t.price}</p>
                    <p className={`font-body text-xs mt-2 ${t.featured ? "text-white/60" : "text-[#2C5A63]/55"}`}>{t.priceNote}</p>
                    <p className={`font-body text-xs mt-1 font-semibold ${t.featured ? "text-white/80" : "text-[#2C5A63]/75"}`}>{t.reps}</p>
                  </div>
                ))}
              </div>

              {/* Comparison rows */}
              {COMPARISON.map((row, ri) => (
                <div
                  key={row.feature}
                  data-testid={`comparison-row-${ri}`}
                  className="grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr] border-b border-[#E4E4EA]"
                >
                  <div className="col-span-2 md:col-span-1 pt-4 pb-1 md:py-3 md:pr-6 font-body text-sm text-[#2C5A63] flex items-center">
                    {row.feature}
                  </div>
                  <TierCell value={row.member} />
                  <TierCell value={row.vip} vip />
                </div>
              ))}

              {/* CTAs */}
              <div className="grid grid-cols-2 md:grid-cols-[1.6fr_1fr_1fr]">
                <div className="hidden md:block" />
                {TIERS.map((t) => (
                  <div
                    key={t.name}
                    className={`px-4 py-6 text-center rounded-b-sm ${
                      t.featured ? "bg-[#2C5A63]" : "bg-[#FAF9F7] border-x border-b border-[#E4E4EA]"
                    }`}
                  >
                    <a
                      href={t.link} target="_blank" rel="noreferrer"
                      data-testid={`tier-cta-${t.name.split(" ")[1].toLowerCase()}`}
                      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${
                        t.featured
                          ? "bg-white text-[#2C5A63] hover:bg-[#7BA7AE] hover:text-white"
                          : "bg-[#2C5A63] text-white hover:bg-[#21474F]"
                      }`}
                    >
                      {t.cta} <ArrowUpRight size={16} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Supercharge / 10X Visibility */}
      <section className="bg-[#F3F2F5] py-20 lg:py-28" data-testid="supercharge">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-12 max-w-3xl">
            <p className="overline text-[#7BA7AE] mb-4">Supercharge your membership</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none">
              10X your visibility
            </h2>
            <p className="font-body text-lg text-[#2C5A63]/80 mt-6 leading-relaxed">
              Your membership gets you in the room. This gets you in front of the couples. A single
              directory listing is one door, and couples walk past it every day. Supercharge puts your
              business in front of them everywhere they're planning — the directory, their inbox,
              their feed, and face to face at the events where they're actively shopping for their
              wedding. Same community. Same connections. A lot more eyes on you.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            <Reveal className="lg:col-span-4">
              <div className="bg-[#2C5A63] text-white h-full flex flex-col items-center justify-center p-12 text-center rounded-sm">
                <span className="font-body font-extrabold text-8xl lg:text-9xl leading-none tracking-tight">
                  10<span className="text-[#7BA7AE]">X</span>
                </span>
                <p className="overline text-[#7BA7AE] mt-4">More eyes on you</p>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-8">
              <div className="bg-white border border-[#E4E4EA] h-full p-9 lg:p-10">
                <p className="font-display text-2xl text-[#2C5A63] mb-6">Everything in your Devoted membership, plus:</p>
                <ul className="space-y-4 font-body text-[#2C5A63]/80">
                  {[
                    ["Multiple directory placements", "Show up in several categories and featured spots instead of one listing in one place."],
                    ["Newsletter features", "Your business in front of thousands of local engaged couples, straight to their inbox."],
                    ["Wedding Resale Market™ visibility", "Reach couples at the events where they show up mid-planning and ready to spend."],
                    ["A dedicated blog feature", "A spotlight post that tells your story and keeps working long after it goes live."],
                    ["Social media spotlights", "Posts across our channels, seen by local couples who are planning right now."],
                    ["And more perks", "Additional visibility designed to keep you top of mind."],
                  ].map(([t, d]) => (
                    <li key={t} className="flex gap-3 border-b border-[#E4E4EA] pb-4">
                      <span className="text-[#7BA7AE] mt-1">✦</span>
                      <span><strong className="text-[#2C5A63]">{t}.</strong> {d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-12 max-w-3xl">
            <h3 className="font-display text-3xl text-[#2C5A63]">Why it works</h3>
            <p className="font-body text-[#2C5A63]/80 mt-4 leading-relaxed">
              Couples don't book the vendor they saw once. They book the one they keep seeing.
              Multi-place visibility is critical because it builds trust, establishes niche authority,
              a first-to-reply advantage, and prevents you from being overlooked. Couples are now
              utilizing vastly different channels depending on where they are in their planning
              journey. When you show up in all of those places, you stop being one option among many
              and become the name they already recognize.
            </p>
            <SuperchargeCTA />
          </Reveal>
        </div>
      </section>

      {/* Facebook Group + Follow */}
      <section className="bg-[#2C5A63] text-white py-10" data-testid="facebook-group">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div>
            <h2 className="font-display text-2xl lg:text-3xl leading-tight">Join our wedding pros community</h2>
            <p className="font-body text-sm text-white/70 mt-1">A private Facebook group just for wedding professionals.</p>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="https://www.facebook.com/share/g/1Cxr73tdCY/"
              target="_blank" rel="noreferrer"
              data-testid="facebook-group-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#2C5A63] px-6 py-3 text-sm font-medium hover:bg-[#7BA7AE] hover:text-white transition-colors whitespace-nowrap"
            >
              Join the group <ArrowUpRight size={16} />
            </a>
            <div className="flex items-center gap-3 border-l border-white/20 pl-5">
              <a href="https://www.facebook.com/devotedweddingpros/" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="follow-facebook" className="text-white/70 hover:text-white transition-colors"><Facebook size={20} /></a>
              <a href="https://www.instagram.com/devotedweddingprosohio/" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="follow-instagram" className="text-white/70 hover:text-white transition-colors"><Instagram size={20} /></a>
              <a href="https://www.linkedin.com/company/weddingpros" target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="follow-linkedin" className="text-white/70 hover:text-white transition-colors"><Linkedin size={20} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Leaders */}
      <section className="bg-[#F3F2F5] py-20 lg:py-28" data-testid="board">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-14">
            <p className="overline text-[#2C5A63] mb-4">Leadership</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none">
              Meet our 2026 leaders
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {BOARD.map((m, i) => (
              <Reveal key={m.name} delay={(i % 3) * 0.08}>
                <div className="text-center" data-testid={`board-${m.name.split(" ")[0].toLowerCase()}`}>
                  <div className="relative aspect-[4/5] max-w-[300px] mx-auto overflow-hidden mb-4 border border-[#E4E4EA]">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105" loading="lazy" />
                  </div>
                  <p className="overline text-[#7BA7AE]">{m.role}</p>
                  <p className="font-display text-xl text-[#2C5A63] mt-1">{m.name}</p>
                  <p className="font-body text-xs text-[#2C5A63]/60">{m.company}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Connect With Devoted Wedding Pros */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="networking-connect">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#2C5A63] mb-4">Connect with Devoted Wedding Pros</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C5A63] tracking-tight leading-none">
              Let's talk about growing together.
            </h2>
            <p className="font-body text-[#2C5A63]/70 mt-6 max-w-md">
              Tell us a little about you and what you're interested in — hosting a community event,
              being an educator, wedding expos, or mentorship — and the Devoted team will follow up.
            </p>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <ConnectForm />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
